import React, { useEffect, useState, useMemo } from "react";
import { Table, Button, Modal, Form, Container, Row, Col, Card, Spinner, Alert, Badge } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaTrash, FaBuilding, FaSearch, FaArrowLeft, FaCog, FaUtensils } from "react-icons/fa";
import { useTipoServicioAlimentacionOrigen } from '../../../services/hooks/useTipoServicioAlimentacionOrigen';
import { useTipoOrigenTrabajadores } from '../../../services/hooks/useTipoOrigenTrabajadores';
import { useClientes } from '../../../services/hooks/useClientes';
import { useTipoServiciosAlimentacion } from '../../../services/hooks/useTipoServiciosAlimentacion';

const MantenimientoTiposServiciosAlimentacionOrigen = () => {
  const navigate = useNavigate();
  
  const { relaciones, loading, fetchRelacionesByCliente, createRelacion, deleteRelacion } = 
    useTipoServicioAlimentacionOrigen();
  const { tiposOrigen, fetchTiposOrigen } = useTipoOrigenTrabajadores();
  const { clientes, fetchClientesByProyecto } = useClientes();
  const { tipoServiciosAlimentacion, fetchTipoServiciosAlimentacion } = useTipoServiciosAlimentacion();
  
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    cliente_id: null,
    tipo_servicio_alimentacion_id: null,
    tipo_origen_id: null,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [relacionToDelete, setRelacionToDelete] = useState(null);
  
  // Obtener proyecto del localStorage - usar useMemo para evitar re-renders innecesarios
  const selectedProject = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("selectedProject") || 'null');
    } catch {
      return null;
    }
  }, []);

  // Cargar clientes cuando cambie el proyecto ID
  const proyectoId = selectedProject?.id;
  useEffect(() => {
    if (proyectoId) {
      fetchClientesByProyecto(proyectoId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proyectoId]);

  // Cargar tipos de origen y servicios una sola vez al montar
  useEffect(() => {
    fetchTiposOrigen();
    fetchTipoServiciosAlimentacion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo al montar el componente

  const handleClienteChange = (e) => {
    const clienteId = parseInt(e.target.value);
    setClienteSeleccionado(clienteId);
    if (clienteId) {
      fetchRelacionesByCliente(clienteId);
    }
  };

  const handleOpenCreateModal = () => {
    if (!clienteSeleccionado) {
      toast.error("Por favor seleccione un cliente primero");
      return;
    }
    setFormData({
      cliente_id: clienteSeleccionado,
      tipo_servicio_alimentacion_id: null,
      tipo_origen_id: null,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      cliente_id: null,
      tipo_servicio_alimentacion_id: null,
      tipo_origen_id: null,
    });
  };

  const handleSave = async () => {
    if (!formData.tipo_servicio_alimentacion_id || !formData.tipo_origen_id) {
      toast.error("Debe seleccionar tanto el tipo de servicio como el tipo de origen");
      return;
    }

    try {
      await createRelacion(formData);
      handleCloseModal();
      if (clienteSeleccionado) {
        await fetchRelacionesByCliente(clienteSeleccionado);
      }
    } catch (error) {
      console.error("Error al guardar:", error);
    }
  };

  const handleDelete = (relacion) => {
    setRelacionToDelete(relacion);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!relacionToDelete) return;
    
    try {
      await deleteRelacion(
        relacionToDelete.cliente_id,
        relacionToDelete.tipo_servicio_alimentacion_id,
        relacionToDelete.tipo_origen_id
      );
      setShowDeleteModal(false);
      setRelacionToDelete(null);
      if (clienteSeleccionado) {
        await fetchRelacionesByCliente(clienteSeleccionado);
      }
    } catch (error) {
      console.error("Error al eliminar:", error);
    }
  };

  const clienteActual = clientes.find(c => c.id === clienteSeleccionado);

  return (
    <>
      {/* Digital Harmony Styles */}
      <style>
        {`
          /* Fondo degradado */
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          
          /* Header principal */
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          
          .dh-main-title {
            font-size: 2.2rem;
            font-weight: 700;
            margin: 0;
          }
          
          .dh-main-subtitle {
            font-size: 1.1rem;
            opacity: 0.9;
            margin: 0.5rem 0 0 0;
          }
          
          /* Cards con Digital Harmony */
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
            margin-bottom: 2rem;
          }
          
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.5rem 2rem;
            border: none;
            font-weight: 700;
          }
          
          .dh-card-header-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          
          /* Botones Digital Harmony */
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 8px 16px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(239, 68, 68, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-danger:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
            color: white;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
          
          /* Campos de formulario modernos */
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
            font-size: 1rem;
            transition: all 0.3s ease;
            background: rgba(255, 255, 255, 0.9);
          }
          
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
            background: white;
          }
          
          /* Enhanced Table */
          .dh-enhanced-table {
            border-collapse: separate;
            border-spacing: 0;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.05);
            background: white;
          }
          
          .dh-enhanced-table thead th {
            background: #374151 !important;
            color: white !important;
            font-weight: 700 !important;
            font-size: 0.875rem !important;
            text-transform: uppercase !important;
            padding: 1rem 0.75rem !important;
            border: none !important;
            position: sticky !important;
            top: 0 !important;
            z-index: 10 !important;
          }
          
          .dh-enhanced-table tbody td {
            padding: 0.75rem;
            border: none;
            border-bottom: 1px solid #f3f4f6;
            font-size: 0.9rem;
            vertical-align: middle;
          }
          
          .dh-enhanced-table tbody tr:hover {
            background: linear-gradient(135deg, #f9fafb 0%, rgba(102, 126, 234, 0.02) 100%) !important;
          }
          
          /* Badge moderno */
          .dh-badge {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-radius: 10px;
            padding: 8px 12px;
            font-weight: 600;
            font-size: 0.85rem;
          }
          
          /* Alert personalizado */
          .dh-alert {
            border-radius: 15px;
            padding: 1rem 1.5rem;
            border: none;
            margin-bottom: 1.5rem;
          }
          
          .dh-alert-info {
            background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 197, 253, 0.1) 100%);
            color: #1e40af;
            border-left: 4px solid #3b82f6;
          }
        `}
      </style>

      <div className="dh-maintenance-bg">
        <ToastContainer />
        
        <div className="dh-main-header">
          <Container>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="dh-main-title">
                  <FaUtensils className="me-3" />
                  Relaciones: Servicios Alimentación - Origen
                </h1>
                <p className="dh-main-subtitle">Configurar políticas por cliente y proyecto</p>
              </div>
              <div className="d-flex align-items-center gap-3">
                <Badge className="dh-badge">
                  Proyecto: {selectedProject?.nombre || "No seleccionado"}
                </Badge>
                <Button 
                  className="btn-dh-secondary d-flex align-items-center"
                  onClick={() => navigate("/home")}
                >
                  <FaArrowLeft className="me-2" />
                  Volver
                </Button>
              </div>
            </div>
          </Container>
        </div>

        <Container fluid className="py-4">
          <Row>
            <Col lg={12}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header dh-card-header-primary">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      <FaCog className="me-2" />
                      Relaciones Configuradas
                    </h5>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          <FaBuilding className="me-2" />
                          Seleccionar Cliente *
                        </Form.Label>
                        <Form.Select
                          value={clienteSeleccionado || ""}
                          onChange={handleClienteChange}
                          className="dh-form-control"
                        >
                          <option value="">-- Seleccione un cliente --</option>
                          {clientes.map((cliente) => (
                            <option key={cliente.id} value={cliente.id}>
                              {cliente.nombre || cliente.nombre_cliente || cliente.razon_social || `Cliente ${cliente.id}`}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6} className="d-flex align-items-end">
                      {clienteSeleccionado && (
                        <Button
                          className="btn-dh-primary d-flex align-items-center"
                          onClick={handleOpenCreateModal}
                        >
                          <FaPlus className="me-2" />
                          Nueva Relación
                        </Button>
                      )}
                    </Col>
                  </Row>

                  {!clienteSeleccionado ? (
                    <Alert variant="info" className="dh-alert dh-alert-info text-center py-5">
                      <FaSearch className="mb-3" size={48} />
                      <p>Por favor seleccione un cliente para ver sus relaciones configuradas</p>
                    </Alert>
                  ) : loading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-3">Cargando relaciones...</p>
                    </div>
                  ) : (
                    <>
                      {clienteActual && (
                        <Alert variant="info" className="dh-alert dh-alert-info mb-3">
                          <strong>Cliente:</strong> {clienteActual.nombre || clienteActual.nombre_cliente || clienteActual.razon_social || `Cliente ${clienteActual.id}`}
                          {selectedProject && (
                            <span className="ms-3">
                              <strong>Proyecto:</strong> {selectedProject.nombre}
                            </span>
                          )}
                        </Alert>
                      )}
                      
                      <div className="table-responsive">
                        <Table className="dh-enhanced-table">
                          <thead>
                            <tr>
                              <th>Tipo de Servicio de Alimentación</th>
                              <th>Tipo de Origen</th>
                              <th>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {relaciones.length === 0 ? (
                              <tr>
                                <td colSpan="3" className="text-center py-4">
                                  No hay relaciones configuradas para este cliente
                                </td>
                              </tr>
                            ) : (
                              relaciones.map((relacion, index) => (
                                <tr key={index}>
                                  <td><strong>{relacion.nombre_tipo_servicio || `ID: ${relacion.tipo_servicio_alimentacion_id}`}</strong></td>
                                  <td><strong>{relacion.nombre_tipo_origen || `ID: ${relacion.tipo_origen_id}`}</strong></td>
                                  <td>
                                    <Button
                                      className="btn-dh-danger d-flex align-items-center"
                                      size="sm"
                                      onClick={() => handleDelete(relacion)}
                                    >
                                      <FaTrash className="me-1" size={12} />
                                      Eliminar
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal para crear relación */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Nueva Relación</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Cliente</Form.Label>
                <Form.Control
                  type="text"
                  value={clienteActual?.nombre || clienteActual?.nombre_cliente || clienteActual?.razon_social || `Cliente ${clienteActual?.id}` || ""}
                  disabled
                  className="dh-form-control"
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Tipo de Servicio de Alimentación *</Form.Label>
                <Form.Select
                  value={formData.tipo_servicio_alimentacion_id || ""}
                  onChange={(e) => setFormData({
                    ...formData,
                    tipo_servicio_alimentacion_id: parseInt(e.target.value)
                  })}
                  className="dh-form-control"
                >
                  <option value="">-- Seleccione --</option>
                  {tipoServiciosAlimentacion.map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>
                      {tipo.descripcion || `ID: ${tipo.id}`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Tipo de Origen *</Form.Label>
                <Form.Select
                  value={formData.tipo_origen_id || ""}
                  onChange={(e) => setFormData({
                    ...formData,
                    tipo_origen_id: parseInt(e.target.value)
                  })}
                  className="dh-form-control"
                >
                  <option value="">-- Seleccione --</option>
                  {tiposOrigen.map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>
                      {tipo.nombre}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button className="btn-dh-primary" onClick={handleSave}>
              Crear Relación
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de confirmación de eliminación */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirmar Eliminación</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            ¿Está seguro de que desea eliminar esta relación?
            {relacionToDelete && (
              <div className="mt-3">
                <strong>Tipo de Servicio:</strong> {relacionToDelete.nombre_tipo_servicio || relacionToDelete.tipo_servicio_alimentacion_id}
                <br />
                <strong>Tipo de Origen:</strong> {relacionToDelete.nombre_tipo_origen || relacionToDelete.tipo_origen_id}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancelar
            </Button>
            <Button className="btn-dh-danger" onClick={confirmDelete}>
              Eliminar
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default MantenimientoTiposServiciosAlimentacionOrigen;
